/**
 * Historial de ventas.
 *
 * ESTA PANTALLA MENTIA
 * --------------------
 * La version anterior tenia una constante `VENTAS` con comprobantes inventados
 * y no consultaba nada. Parecia terminada, mostraba totales, y ninguno de esos
 * numeros existia.
 *
 * Es la peor forma de estar incompleto: una pantalla vacia se nota, una pantalla
 * que muestra datos falsos no. Alguien podia mirar el total del dia y tomar una
 * decision con el.
 *
 * Ahora lee `GET /ventas`.
 */
import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { ClipboardList, RotateCcw } from "lucide-react";

import { formatear } from "@tora/shared";
import { Aviso } from "@/components/ui/aviso";
import { PageHeader } from "@/components/layout/PageHeader";
import { Pagina } from "@/components/layout/Pagina";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { InputBusqueda } from "@/components/ui/input-busqueda";
import { Label } from "@/components/ui/label";
import { PieDeLista } from "@/components/ui/pie-de-lista";
import { Segmented } from "@/components/ui/segmented";
import { SelectorPeriodo } from "@/components/ui/selector-periodo";
import { DetalleVenta } from "@/components/venta/DetalleVenta";
import { DialogNotaCredito } from "@/components/venta/DialogNotaCredito";
import {
  ListaVentas,
  ProveedorEncargosDeVentas,
  type EncargoDeVenta,
} from "@/components/venta/ListaVentas";
import { esteMes, rangoDelPeriodo, type Periodo } from "@/lib/periodo";
import { aNumero, type TipoComprobante } from "@/lib/tipos";
import {
  useVentas,
  useVenta,
  useAnularComprobante,
  useNotaCredito,
  useClientes,
  useCuentasFinancieras,
  useEncargos,
  useSesion,
} from "@/lib/queries";
import { mostrarError, mostrarExito } from "@/lib/errores";
import { useVerMas } from "@/lib/usar-ver-mas";

/**
 * `?q=VENTA-0000103` abre la pantalla ya buscando: es a donde lleva un
 * comprobante elegido en el buscador global (⌘K).
 */
interface BusquedaVentas {
  q?: string;
}

export const Route = createFileRoute("/ventas")({
  component: Ventas,
  validateSearch: (busqueda: Record<string, unknown>): BusquedaVentas =>
    typeof busqueda.q === "string" && busqueda.q.trim() !== "" ? { q: busqueda.q.trim() } : {},
});

type Vista = "TODO" | TipoComprobante;

const VISTAS: ReadonlyArray<{ valor: Vista; etiqueta: string }> = [
  { valor: "TODO", etiqueta: "Todo" },
  { valor: "VENTA", etiqueta: "Ventas" },
  { valor: "NC", etiqueta: "Notas de crédito" },
  { valor: "ND", etiqueta: "Notas de débito" },
];

function Ventas(): JSX.Element {
  const { q } = Route.useSearch();
  const [vista, setVista] = useState<Vista>("TODO");
  const [texto, setTexto] = useState(q ?? "");
  // Elegir otro comprobante en ⌘K estando ya en Ventas cambia el `q` sin
  // volver a montar la pantalla. Se ajusta en el render, no en un efecto.
  const [qVisto, setQVisto] = useState(q);
  if (q !== qVisto) {
    setQVisto(q);
    if (q !== undefined) setTexto(q);
  }
  // Arranca en el mes corriente: la pantalla se abre para ver lo de este mes,
  // no para revisar los ultimos 200 comprobantes de la historia.
  const [periodo, setPeriodo] = useState<Periodo>(() => esteMes());
  const [anulando, setAnulando] = useState<number | null>(null);
  const [acreditando, setAcreditando] = useState<number | null>(null);

  const tipo = vista === "TODO" ? undefined : vista;
  const busqueda = texto.trim();
  const buscando = busqueda !== "";

  /**
   * BUSCAR IGNORA EL PERIODO.
   *
   * Mientras hay texto en el buscador no viajan `desde` ni `hasta`: se busca en
   * todo el historial. "Buscar" significa buscar; si el numero que se escribe
   * tuviera que estar ademas dentro del mes elegido, encontrar una venta vieja
   * serian siempre dos pasos, y el primero —darse cuenta de que hay que mover
   * el periodo— no lo sugiere nada en pantalla.
   *
   * El backend no sabe de esta regla: si le llegan los tres parametros filtra
   * por los tres. La decision es de esta pantalla, y por eso esta escrita aca.
   */
  const rango = buscando ? {} : rangoDelPeriodo(periodo);

  // La ventana de filas. Cambiar un filtro la devuelve a la primera pagina:
  // seguir en la pagina 3 del filtro anterior es un recorte que nadie pidio.
  const { limite, verMas, reiniciar } = useVerMas(200);
  useEffect(() => reiniciar(), [tipo, busqueda, periodo, reiniciar]);

  // `useVentas` conserva los datos anteriores mientras cambia el filtro
  // (`keepPreviousData`): `isLoading` es solo la primera carga; despues, la
  // lista se atenua con `isFetching` y nunca se vacia para volver a llenarse.
  // Las ventas con algo a pedido que no llegó o que falta entregar (0111).
  const { data: porRecibir, isError: fallaRecibir } = useEncargos({ estado: "POR_RECIBIR" });
  const { data: porEntregar, isError: fallaEntregar } = useEncargos({ estado: "POR_ENTREGAR" });
  const encargosPorVenta = useMemo(() => {
    const m = new Map<number, EncargoDeVenta>();
    for (const e of porEntregar?.filas ?? []) m.set(e.comprobante_id, "POR_ENTREGAR");
    // Lo que falta que llegue pesa más: si una venta tiene de las dos, dice esto.
    for (const e of porRecibir?.filas ?? []) m.set(e.comprobante_id, "POR_RECIBIR");
    return m;
  }, [porRecibir, porEntregar]);

  const { data, isLoading, isFetching } = useVentas({
    tipo,
    // La busqueda va a la BASE, no al array ya cargado: filtrar en el cliente
    // sobre los ultimos 200 hacia que un comprobante viejo diera "no encontre"
    // existiendo. Ademas mira el nombre del cliente, que en la fila se resuelve
    // por `cliente_id` y por lo tanto aca nunca se podria haber buscado.
    ...(buscando ? { q: busqueda } : {}),
    ...rango,
    limite,
  });
  // La lista de `/ventas` trae solo `cliente_id`; el nombre sale de la lista
  // de clientes, que ya esta en cache por la pantalla de Clientes.
  const { data: clientes } = useClientes({ limite: 200 });
  const anular = useAnularComprobante();
  const notaCredito = useNotaCredito();
  // El detalle de la venta que se esta acreditando, y las cajas por si no
  // tiene cliente. Las dos consultas duermen hasta que se abre el dialogo.
  const ventaAAcreditar = useVenta(acreditando ?? undefined);
  const { data: cuentas } = useCuentasFinancieras();
  const { data: sesion } = useSesion();
  const esAdmin = sesion?.rol === "admin";

  const filas = data?.filas ?? [];

  const nombresClientes = useMemo(
    () => new Map((clientes?.filas ?? []).map((c) => [c.id, c.nombre])),
    [clientes],
  );

  const ventaElegida = acreditando === null ? undefined : filas.find((c) => c.id === acreditando);

  const hayFiltros = busqueda !== "" || vista !== "TODO" || periodo.tipo !== "todo";
  function limpiarFiltros(): void {
    setTexto("");
    setVista("TODO");
    setPeriodo({ tipo: "todo" });
  }

  return (
    /*
      `alto="fijo"`: la pagina no scrollea, scrollea la lista. Antes el `<main>`
      scrolleaba Y la tabla scrolleaba adentro con un techo de 70dvh propio, asi
      que en un notebook habia dos barras y el pie sticky —el de los totales—
      podia quedar debajo del pliegue. Un sticky que no se ve no es un sticky.
    */
    <Pagina alto="fijo">
      <PageHeader titulo="Ventas" icono={ClipboardList} />

      {/*
        Los tres controles en UN renglon. Si esto se parte en dos, el alto que
        gana la pantalla fija se lo come la barra de filtros.
      */}
      <div className="flex flex-wrap items-center gap-2">
        <InputBusqueda
          valor={texto}
          onValorChange={setTexto}
          etiqueta="Buscar comprobante por número o por cliente"
          placeholder="Buscar por número o cliente"
          className="min-w-48 max-w-xs flex-1"
        />
        <Segmented
          apariencia="chips"
          tamano="sm"
          etiqueta="Tipo de comprobante"
          opciones={VISTAS}
          valor={vista}
          onChange={setVista}
        />
        {/*
          Atenuado mientras se busca, porque en ese momento el periodo no esta
          filtrando nada: es lo unico que explica por que aparecen comprobantes
          de otro mes.
        */}
        <SelectorPeriodo valor={periodo} onChange={setPeriodo} atenuado={buscando} />
      </div>

      {/* Sin la lista, ninguna venta dice «falta que llegue»: no es que no haya
          encargos, es que no se pudieron leer. Antes callaba. */}
      {fallaRecibir || fallaEntregar ? (
        <Aviso tono="warning" compacto>
          No se pudo leer la lista de encargos: las ventas a pedido no muestran si falta que lleguen
          o que se entreguen. Recargá la página.
        </Aviso>
      ) : null}

      <ProveedorEncargosDeVentas value={encargosPorVenta}>
        <ListaVentas
          filas={filas}
          esAdmin={esAdmin}
          nombresClientes={nombresClientes}
          totales={data?.totales}
          cargando={isLoading}
          actualizando={isFetching && !isLoading}
          mensajeVacio={
            busqueda
              ? `No encontré ningún comprobante con «${busqueda}» en el número ni en el cliente.`
              : "Todavía no hay comprobantes."
          }
          onLimpiarFiltros={hayFiltros ? limpiarFiltros : undefined}
          pie={
            <PieDeLista
              visibles={filas.length}
              total={data?.total ?? 0}
              sustantivo="comprobantes"
              onVerMas={verMas}
              cargando={isFetching}
            />
          }
          onNotaCredito={setAcreditando}
          onAnular={setAnulando}
          renderDetalle={(c) => <DetalleVenta comprobante={c} cuentas={cuentas?.filas ?? []} />}
        />
      </ProveedorEncargosDeVentas>

      {acreditando !== null && (
        <DialogNotaCredito
          venta={{
            id: acreditando,
            numero: ventaElegida?.numero ?? "",
            moneda: ventaElegida?.moneda ?? "ARS",
            total: aNumero(ventaElegida?.total ?? 0),
            cliente_id: ventaElegida?.cliente_id ?? null,
          }}
          detalle={ventaAAcreditar.data?.detalle ?? []}
          cargando={ventaAAcreditar.isLoading}
          cuentas={cuentas?.filas ?? []}
          guardando={notaCredito.isPending}
          onCerrar={() => setAcreditando(null)}
          onConfirmar={(datos) =>
            notaCredito.mutate(
              { id: acreditando, datos },
              {
                onSuccess: (r) => {
                  mostrarExito(
                    `Nota de crédito ${r.numero} emitida`,
                    r.devuelto
                      ? `Salieron ${formatear(aNumero(r.devuelto.importe), r.devuelto.moneda)} de ${r.devuelto.cuenta}.`
                      : "Se acreditó en la cuenta corriente del cliente.",
                  );
                  setAcreditando(null);
                },
                onError: mostrarError,
              },
            )
          }
        />
      )}

      {anulando !== null && (
        <DialogAnular
          id={anulando}
          numero={filas.find((c) => c.id === anulando)?.numero ?? ""}
          guardando={anular.isPending}
          onCerrar={() => setAnulando(null)}
          onConfirmar={(motivo) =>
            anular.mutate(
              { id: anulando, motivo },
              {
                onSuccess: () => {
                  mostrarExito("Comprobante anulado", "Se repuso el stock y se borró el asiento.");
                  setAnulando(null);
                },
                onError: mostrarError,
              },
            )
          }
        />
      )}
    </Pagina>
  );
}

function DialogAnular({
  numero,
  guardando,
  onCerrar,
  onConfirmar,
}: {
  id: number;
  numero: string;
  guardando: boolean;
  onCerrar: () => void;
  onConfirmar: (motivo: string) => void;
}): JSX.Element {
  const [motivo, setMotivo] = useState("");

  // Sobre `ui/dialog` (Radix): Escape cierra, el foco queda atrapado adentro y
  // vuelve al boton que lo abrio. Es una accion destructiva: no se sale con Tab.
  return (
    <Dialog open onOpenChange={(abierto) => !abierto && !guardando && onCerrar()}>
      <DialogContent tamano="sm">
        <DialogHeader>
          <DialogTitle>Anular {numero}</DialogTitle>
          <DialogDescription>
            Se repone el stock que la venta había descontado y se borra su asiento en la cuenta
            corriente del cliente.
          </DialogDescription>
        </DialogHeader>

        <Card variant="muted" padding="compacto" className="text-xs text-muted-foreground">
          <p>
            <span className="font-medium text-foreground">Si el comprobante ya tiene cobros</span>,
            el sistema lo va a rechazar: en ese caso corresponde una nota de crédito, que deja
            rastro en vez de hacer desaparecer la operación.
          </p>
        </Card>

        <div className="space-y-1.5">
          <Label htmlFor="motivo">Motivo</Label>
          <Input
            id="motivo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Por qué se anula"
            autoFocus
          />
          <p className="text-xs text-muted-foreground">
            Queda un hueco en la numeración: el motivo es lo que lo explica.
          </p>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={motivo.trim().length < 3}
            cargando={guardando}
            onClick={() => onConfirmar(motivo.trim())}
          >
            {guardando ? null : <RotateCcw aria-hidden="true" />}
            Anular
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
